import { gql } from "@apollo/client";

/**
 * One situation's fields, in two versions.
 *
 * Split rather than branched inside one document, because `forGrownup`, `mark`,
 * `sourcePrompt` and `sourceCounter` are guardian-only on the server: a child's
 * request carrying any of them fails the **whole** query, not the one field.
 */
export const SITUATION_FIELDS = gql`
  fragment SituationFields on Situation {
    id
    key
    title
    themeLabels
    intro
    story
    personalized
    sessionId
    questionCount
    answeredCount
    submittedAt
    questions {
      id
      key
      prompt
      answerType
      options
      asksWhy
      counter
      allowsOther
      lines
      answer
      reasonAnswer
      counterAnswer
    }
  }
`;

export const SITUATION_FIELDS_FOR_GROWNUP = gql`
  fragment SituationFieldsForGrownUp on Situation {
    id
    key
    title
    themeLabels
    intro
    story
    personalized
    personalizationTheme
    personalizationStale
    sourceStory
    sessionId
    personalizedFrom {
      grade
      intro
      story
      questions {
        key
        prompt
      }
    }
    forGrownup
    questionCount
    answeredCount
    needsPracticeCount
    submittedAt
    reviewedAt
    questions {
      id
      key
      prompt
      sourcePrompt
      personalized
      answerType
      options
      asksWhy
      counter
      sourceCounter
      allowsOther
      lines
      answer
      reasonAnswer
      counterAnswer
      mark
    }
  }
`;

/**
 * The Sprendimų laboratorija for one learner: the assigned **sessions** and the
 * **catalogue** they are drawn from.
 *
 * Both in one query, deliberately. The session is the guided path — three
 * unanswered situations from different areas, assembled by a grown-up — and the
 * catalogue stays beside it for a child who would rather go looking. Two queries
 * for one screen could disagree about whether the section is switched on at all.
 *
 * The catalogue is not grouped by school year: a friend who will not take no for
 * an answer is the same problem at seven and at seventeen, and a list headed
 * „1 klasė" tells a twelve-year-old they are being handed a first-grader's
 * problem. The year decides the words, never the shelf.
 *
 * `enabled` comes back even when false — a screen deciding whether to draw a nav
 * item needs an answer rather than an error.
 */
export const GET_DECISION_LAB = gql`
  query GetDecisionLab($studentId: ID!, $grade: Int) {
    decisionLab(studentId: $studentId, grade: $grade) {
      enabled
      learnerGrade
      sessions {
        id
        size
        doneCount
        done
        started
        nextKey
        personalized
        createdAt
        situations {
          id
          key
          title
          themeLabels
          answeredCount
          questionCount
          submittedAt
        }
      }
      themes {
        key
        name
        intro
        situations {
          id
          key
          title
          questionCount
          answeredCount
          submittedAt
        }
      }
    }
  }
`;

/** The same, plus what a grown-up needs to assemble and review. */
export const GET_DECISION_LAB_FOR_GROWNUP = gql`
  query GetDecisionLabForGrownUp($studentId: ID!, $grade: Int) {
    decisionLab(studentId: $studentId, grade: $grade) {
      enabled
      learnerGrade
      sessions {
        id
        size
        doneCount
        done
        started
        nextKey
        personalized
        personalizationTheme
        needsPracticeCount
        createdAt
        situations {
          id
          key
          title
          themeLabels
          answeredCount
          questionCount
          needsPracticeCount
          submittedAt
          reviewedAt
        }
      }
      themes {
        key
        name
        intro
        situations {
          id
          key
          title
          questionCount
          answeredCount
          needsPracticeCount
          answeredGrades
          availableGrades
          submittedAt
          reviewedAt
        }
      }
    }
  }
`;

/**
 * Just the assigned sets, for the child's home card.
 *
 * Deliberately not `GET_DECISION_LAB`: that one also builds the catalogue — every
 * problem, with a variant loaded per problem — and a card saying „one is waiting"
 * must not pay for it. `DecisionLabType` resolves each list in its own field
 * method, so asking for one skips the other entirely.
 */
export const GET_LAB_SESSIONS = gql`
  query GetLabSessions($studentId: ID!) {
    decisionLab(studentId: $studentId) {
      enabled
      sessions {
        id
        size
        doneCount
        done
        started
      }
    }
  }
`;

/**
 * One session, with every sheet in it.
 *
 * This is where a personalized reading is served: the wording lives on the
 * session, so the same problem opened from the catalogue reads as the file wrote
 * it and opened from here reads as it was rewritten.
 */
export const GET_SITUATION_SESSION = gql`
  ${SITUATION_FIELDS}
  query GetSituationSession($id: ID!) {
    situationSession(id: $id) {
      id
      studentId
      size
      doneCount
      done
      started
      nextKey
      personalized
      createdAt
      situations {
        ...SituationFields
      }
    }
  }
`;

export const GET_SITUATION_SESSION_FOR_GROWNUP = gql`
  ${SITUATION_FIELDS_FOR_GROWNUP}
  query GetSituationSessionForGrownUp($id: ID!) {
    situationSession(id: $id) {
      id
      studentId
      size
      doneCount
      done
      started
      nextKey
      personalized
      personalizationTheme
      needsPracticeCount
      createdAt
      situations {
        ...SituationFieldsForGrownUp
      }
    }
  }
`;

/**
 * One situation. `sessionId` is what decides whose wording is served — the
 * session's rewrite when it holds this problem, the file's otherwise. A session
 * that does not contain it is ignored rather than refused, so a stale link serves
 * a readable sheet instead of an error in front of a child.
 */
export const GET_SITUATION = gql`
  ${SITUATION_FIELDS}
  query GetSituation($studentId: ID!, $key: String!, $sessionId: ID) {
    situation(studentId: $studentId, key: $key, sessionId: $sessionId) {
      ...SituationFields
    }
  }
`;

export const GET_SITUATION_FOR_GROWNUP = gql`
  ${SITUATION_FIELDS_FOR_GROWNUP}
  query GetSituationForGrownUp($studentId: ID!, $key: String!, $sessionId: ID) {
    situation(studentId: $studentId, key: $key, sessionId: $sessionId) {
      ...SituationFieldsForGrownUp
    }
  }
`;
